import type { NextPage } from "next";
import { useRouter } from "next/dist/client/router";
import * as Sentry from "@sentry/nextjs";
import PublicNavbarLayout from "../../components/layout/PublicNavbarLayout";
import StepOne from "./Steps/StepOne";
import StepTwo from "./Steps/StepTwo/StepTwo";
import StepThree from "./Steps/StepThree";
import StepFour from "./Steps/StepFour";
import StepFive from "./Steps/StepFive/StepFive";
import FinalStep from "./Steps/FinalStep";
import { useForm } from "react-hook-form";
import { CreateStripeCheckoutUrlDocument, IsEmailAvailableDocument, useRegisterMutation } from "../../lib/api/sdk";
import { useErrorToast } from "../../lib/utils/toasts/toasts";
import { useEffect, useState } from "react";
import { Box, Center, VStack } from "@chakra-ui/react";
import Stepper from "./Components/Stepper";
import { IfAdd } from "../../lib/conditional-expanders/conditionalExpanders";
import {
  StepWithoutSlider,
  StepWithSlider,
} from "./Components/LayoutComponents";
import LoadingCircular from "../../components/LoadingCircular";
import RegisterController, { getPendingFormData } from "../../lib/controllers/RegisterController";
import { useApolloClient } from "@apollo/client";
import { getPaths } from "lib/utils/paths";
import { useTranslations } from "next-intl";
import useTranslationConstants from "lib/utils/constants";
import { useUser } from "lib/contexts/useUser";
import { useLocales } from "src/contexts/LocalesContext";

export const INPUT_VARIANT = "outline";
export const INPUT_SIZE = "lg";
export const INPUT_SPACING = "4";

interface IRenderStepProps {
  step: string;
  RegisterFormController: any;
}

const RegisterStepper = ({
  activeStep,
  justifyContent
}: {
  activeStep: number,
  justifyContent?: string | string[]
}) => {
  return <Stepper totalSteps={5} step={activeStep} color="brandOrange.500" justifyContent={justifyContent} />;
};

const RenderStepContent = ({
  step,
  RegisterFormController,
}: IRenderStepProps) => {
  const { DEFAULT_SEDENTARY_REST_HEART_RATE, DEFAULT_ATHLETE_REST_HEART_RATE } = useTranslationConstants();
  const { push } = useRouter();
  const errorToast = useErrorToast();
  const apolloClient = useApolloClient();
  const t = useTranslations("pages.register");

  const registration_done = localStorage.getItem("registration_done");

  const [Register] = useRegisterMutation();

  const [registerStatus, setRegisterStatus] = useState<any>({
    loading: false,
    error: null,
  });

  const onSubmitStepOne = async (formData: any) => {
    try {
      const res = await apolloClient.query({
        query: IsEmailAvailableDocument,
        variables: { email: formData.email },
        fetchPolicy: "network-only",
      });
      if (!res.data?.is_email_available) {
        throw new Error(
          t("errorMessage", {
            email: formData.email
          })
        );
      }
      RegisterController.onSubmitStepOne(formData, push);
    } catch (error) {
      errorToast({ error });
      setRegisterStatus({ ...registerStatus, error, loading: false });
    }
  };

  const processStripePayment = async (
    email: string,
    stripe_price_id: string,
    email_language: string
  ) => {
    try {
      const stripeUrl = await apolloClient.query({
        query: CreateStripeCheckoutUrlDocument,
        variables: { email, stripe_price_id, email_language },
        fetchPolicy: "network-only"
      });
      setRegisterStatus({ ...registerStatus, loading: false });
      window.location.href = stripeUrl.data.create_stripe_checkout_url;
    } catch (error) {
      setRegisterStatus({ ...registerStatus, loading: false });
      throw new Error(t("cantCheckoutMessage"));
    }
  };

  const onSubmitStepFive = async () => {
    setRegisterStatus({ ...registerStatus, loading: true });
    const formValues = RegisterFormController.getValues();

    if (!formValues.stripe_price_lookup_key) return;

    if (registration_done) {
      processStripePayment(
        formValues.email,
        formValues.stripe_price_lookup_key,
        formValues.email_language
      );
      return;
    }

    const birthday = `${formValues.bday_year}-${formValues.bday_month}-${formValues.bday_day?.length < 2 ? 0 : ""
      }${formValues.bday_day}`;
    try {
      const variables = {
        inputs: {
          name: formValues.name,
          phone: formValues.phone,
          email: formValues.email,
          email_language: formValues.email_language,
          password: formValues.password,
          birthday: new Date(birthday),
          goal: formValues.goal,
          sport_id: formValues.sport_id,
          height: +formValues.height,
          weight: +formValues.weight,
          max_heart_rate: +formValues.max_heart_rate,
          rest_heart_rate: !isNaN(+formValues.rest_heart_rate)
            ? +formValues.rest_heart_rate : formValues.type === "athlete"
              ? DEFAULT_ATHLETE_REST_HEART_RATE
              : DEFAULT_SEDENTARY_REST_HEART_RATE,
          fiscal_id_number: formValues.fiscal_id_number,
          address_street: formValues.address_street,
          address_postal_code: formValues.address_postal_code,
          address_city: formValues.address_city,
          address_country: formValues.address_country,
          type: formValues.type,
          ...IfAdd(!!formValues.coach_id, {
            coach_id: +formValues.coach_id,
          }),
          gender: formValues.gender
        },
      };

      // If the variables don't include a coach_id, send a message to Sentry
      if (!variables.inputs.coach_id) {
        // eslint-disable-next-line i18next/no-literal-string
        Sentry.captureMessage("No coach_id in Registration process", {
          extra: { data: variables.inputs },
        });
      }

      const RegisterRes = await Register({ variables });

      if (RegisterRes.data?.user_register?.success) {
        // eslint-disable-next-line i18next/no-literal-string
        localStorage.setItem("registration_done", "true");
        // eslint-disable-next-line i18next/no-literal-string
        localStorage.setItem("pending_form_data", JSON.stringify(formValues));
        processStripePayment(
          formValues.email,
          formValues.stripe_price_lookup_key,
          formValues.email_language
        );
        return;
      }
      setRegisterStatus({ ...registerStatus, loading: false });
    } catch (err: any) {
      const error = err?.message?.includes("user_email_type_key")
        ? new Error(
          t("errorMessage", {
            email: formValues.email
          })
        )
        : err;
      setRegisterStatus({ ...registerStatus, error, loading: false });
      errorToast({ error });
    }
  };

  const goBack = () => push(
    getPaths.register.athlete.step(+step - 1),
    undefined,
    { shallow: true },
  );

  switch (step) {
    case "1":
      return (
        <StepOne
          onSubmit={onSubmitStepOne}
          goBack={goBack}
          FormController={RegisterFormController}
          RegisterMutationResult={registerStatus}
        />
      );
    case "2":
      return (
        <StepTwo
          onSubmit={formData =>
            RegisterController.onSubmitStepTwo(formData, push)
          }
          goBack={goBack}
          FormController={RegisterFormController}
        />
      );
    case "3":
      return (
        <StepThree
          onSubmit={formData =>
            RegisterController.onSubmitStepThree(formData, push)
          }
          goBack={goBack}
          FormController={RegisterFormController}
        />
      );
    case "4":
      return (
        <StepFour
          onSubmit={formData =>
            RegisterController.onSubmitStepFour(formData, push)
          }
          goBack={goBack}
          FormController={RegisterFormController}
        />
      );
    case "5":
      return (
        <StepFive
          onSubmit={onSubmitStepFive}
          goBack={goBack}
          disabled={!!registration_done}
          FormController={RegisterFormController}
          RegisterMutationResult={registerStatus}
        />
      );
    case "6":
      return (
        <FinalStep
          FormController={RegisterFormController}
        />
      );

    default:
      return null;
  }
};

const RenderStep = ({
  step,
  RegisterFormController,
}: IRenderStepProps) => {
  switch (step) {
    case "1":
    case "2":
    case "3":
    case "4":
    case "6":
      return (
        <StepWithSlider color="brandOrange.900">
          <VStack w="full">
          {step !== "6" && <RegisterStepper activeStep={+step - 1} />}
            <RenderStepContent
              RegisterFormController={RegisterFormController}
              step={step}
            />
          </VStack>
        </StepWithSlider>
      );

    case "5":
      return (
        <StepWithoutSlider>
          <VStack w="full">
            <RegisterStepper
              activeStep={+step - 1}
              justifyContent={["flex-start", "flex-start", "flex-start", "center"]}
            />
            <RenderStepContent
              RegisterFormController={RegisterFormController}
              step={step}
            />
          </VStack>
        </StepWithoutSlider>
      );

    default:
      return (
        <Center w="full" h="full" flexGrow={1} py={20}>
          <LoadingCircular size={50} />
        </Center>
      );
  }
};

const RegisterPage: NextPage = () => {
  const { loggedIn, signOut } = useUser();
  const { currentLocale, fallback } = useLocales();
  const { query } = useRouter();
  const RegisterFormController = useForm({
    defaultValues: getPendingFormData(currentLocale?.code || fallback),
  });

  useEffect(() => {
    const step = Number(query.step);
    if (step === 1) {
      localStorage.removeItem("registration_done");
      localStorage.removeItem("pending_form_data");
    }
    if (loggedIn && step !== 6) signOut();
  }, [query.step, loggedIn]);

  useEffect(() => {
    if (query.sportId)
      RegisterFormController.setValue("sport_id", query.sportId);
    if (query.coachId)
      RegisterFormController.setValue("coach_id", +query.coachId);
    if (query.subscriptionPlanId)
      RegisterFormController.setValue("plan_id", +query.subscriptionPlanId);
  }, [query]);

  return (
    <PublicNavbarLayout isDark>
      <Box mt={["80px", "80px", "80px", "100px"]}>
        <RenderStep
          step={String(query.step)}
          RegisterFormController={RegisterFormController}
        />
      </Box>
    </PublicNavbarLayout>
  );
};

export default RegisterPage;
